// GENERAL
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// COMPONENTS
import { Section, BaseButton, BaseText } from "../../../components";
import UpdateShipmentForm from "./components/forms/EditShipmentForm";
import { LoadingMessage } from "../../../components";

// UTILS
import getFontAwesomeIcon from "../../../utils/getFontAwesomeIcon";

// TYPES
import {
	ShippingCarrier,
	State,
	Country,
	UpdatedState,
	ModifiedStatesOptions,
	ErrorMessage,
} from "../types";

// API
import {
	getAddress,
	getCountries,
	getShipmentDetails,
	getShippingCarriers,
	getStates,
	getItemFulfillmentCount,
} from "../api/UpdateShipmentApi";

const EditShipmentPage: React.FC = () => {
	///// STATES /////
	const [address, setAddress] = useState<UpdatedState[]>([]);
	const [shippingCarriers, setShippingCarriers] = useState<ShippingCarrier[]>(
		[]
	);
	const [states, setStates] = useState<ModifiedStatesOptions | null>();
	const [countries, setCountries] = useState<Country[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [hasError, setHasError] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<ErrorMessage | null>(null);
	const [shipmentNumber, setShipmentNumber] = useState<string>("");

	///// HOOKS /////
	const query = new URLSearchParams(useLocation().search);
	const internalId = query.get("internalId");
	const navigate = useNavigate();

	////// HELPER FUNCTIONS /////
	const handleError = (error: any) => {
		if (error && error.message && error.status) {
			setErrorMessage(error);
		} else {
			setErrorMessage({
				message: "Unknown error",
				status: "Unknown status",
			});
		}
	};

	// Helper function to ensure no null values are returned in options array
	const checkOptionsArrays = (optionsArray: any) => {
		if (optionsArray) {
			for (const option of optionsArray) {
				if (option === null) {
					return "No options provided";
				}
			}
			return optionsArray;
		}
		return false;
	};

	// Helper function to fetch item fulfillment count
	const fetchItemFulfillmentCount = async () => {
		try {
			const itemFulfillmentCount = await getItemFulfillmentCount(
				internalId || ""
			);
			setShipmentNumber(itemFulfillmentCount);
		} catch (error: any) {
			setHasError(true);
			handleError(error);
			setShipmentNumber("Error fetching count");
		}
	};

	// Helper function to fetch address, reformat data to flatten, and ensure no null values returned
	const fetchAndTransformAddress = async (internalId: string) => {
		const address = await getAddress(internalId || "");
		if (
			address.salesOrders &&
			address.salesOrders[0] &&
			address.salesOrders[0].shipToAddress
		) {
			const shipToAddress = address.salesOrders[0].shipToAddress;
			const attention = address.salesOrders[0].shipToAddress;
			const transformedAddress = {
				...shipToAddress,
				...attention,
				state: {
					uuid: shipToAddress.state?.uuid || "",
					name: shipToAddress.state?.name || "",
				},
				street: shipToAddress.street || "",
				city: shipToAddress.city || "",
				zip: shipToAddress.zip || "",
				uuid: shipToAddress.uuid || "",
			};

			for (const key in transformedAddress) {
				if (
					transformedAddress[key] === null ||
					transformedAddress[key] === undefined
				) {
					transformedAddress[key] = "";
				}
			}

			setAddress(transformedAddress);
		}
	};

	const fetchData = async () => {
		setIsLoading(true);
		setHasError(false);
		setErrorMessage(null);

		try {
			// Fetch all required data in parallel
			const [
				shipmentDetails,
				shippingCarriersData,
				statesData,
				countriesData,
			] = await Promise.all([
				getShipmentDetails(internalId || ""),
				getShippingCarriers(),
				getStates(),
				getCountries(),
			]);

			// Process and set shipping carriers
			const carrierOptions = shippingCarriersData.shippingCarriers
				? {
						shippingCarriers:
							shippingCarriersData.shippingCarriers.slice(0, 1),
				  }
				: [];
			setShippingCarriers(carrierOptions as any);

			// Process and set states
			const updatedStates = {
				states: statesData.states.map((state: State) => ({
					uuid: state.uuid,
					name: state.name,
				})),
			};
			setStates(updatedStates);

			// Set countries
			setCountries(countriesData);

			// Fetch and transform address
			await fetchAndTransformAddress("" + internalId);

			// Fetch item fulfillment count
			await fetchItemFulfillmentCount();
		} catch (error: any) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchData();
	}, [internalId, navigate]);

	///// TEXT THAT WILL COME FROM FIELDS /////
	const returnToSelectItemsButtonText = "Select Items";
	const pageHeadingTwo = "Add serial numbers and update shipment info.";
	const errorHeading = "Something went wrong while fetching the data.";
	const pageTitle = "Update Info";

	///// DATA TO VARS /////
	const shipmentItemsUrl = `/shipment-items?internalId=${internalId}`;
	const optionsArray = [shippingCarriers, states, countries];

	///// CLASS NAMES /////
	const contentSectionClasses = `flex flex-col items-start bg-navy-100 border-1 border-navy-200 rounded-xl w-[1820px] max-[189px]:w-[1600px] overflow-scroll mb-8`;
	const pageClasses =
		"flex justify-center pt-24 px-60px min-h-full max-h-full font-omnes-regular mb-10 overflow-x-scroll";
	const buttonSectionClasses = "flex flex-col items-start pb-5 pt-0";
	const formSectionClasses =
		"flex flex-col items-start w-full max-[1730px]:w-[1520px]";
	const returnToSelectItemsButtonClasses =
		"text-supply-blue-500 hover:text-supply-blue-300";
	const errorContainerClasses =
		"w-full h-full flex flex-col items-start justify-start";
	const loadingContainerClasses = "flex items-center justify-center w-full ";

	return (
		optionsArray && (
			<AnimatePresence>
				<div className={pageClasses}>
					<Section
						additionalClasses={contentSectionClasses}
						padding="p-10"
					>
						<div className="flex justify-between w-full">
							<BaseButton
								text={returnToSelectItemsButtonText}
								fontColor="supply-blue-500"
								additionalClasses={
									returnToSelectItemsButtonClasses
								}
								icon={getFontAwesomeIcon(
									"chevronLeft",
									"regular"
								)}
								iconClasses="text-xl pr-2"
								fontFamily="font-omnes-regular"
								as="link"
								to={shipmentItemsUrl}
							/>
							{internalId && (
								<BaseText
									text={`SO #${internalId}`}
									size="16px"
									color="text-navy-500"
									tag="p"
									fontFamily="Omnes"
								/>
							)}
						</div>
						<Section additionalClasses={buttonSectionClasses}>
							<BaseText
								text={pageTitle}
								additionalClasses="py-4"
								size="text-32px"
								color="text-navy-500"
								tag="h1"
								fontFamily="font-omnes-medium"
							/>
							{!hasError && !isLoading && (
								<BaseText
									text={pageHeadingTwo}
									size="text-base"
									color="text-navy-500"
									tag="h1"
									fontFamily="font-omnes-regular"
								/>
							)}
						</Section>
						<Section
							additionalClasses={formSectionClasses}
							padding="mb-5"
						>
							{hasError ? (
								<div className={errorContainerClasses}>
									<BaseText
										text={errorHeading}
										size="text-lg"
										color="text-navy-500"
										tag="h1"
										fontFamily="font-omnes-medium"
										additionalClasses="mb-2"
									/>
									{errorMessage && (
										<>
											<BaseText
												text={errorMessage.message}
												additionalClasses=""
												size="text-md"
												color="text-navy-500"
												tag="p"
												fontFamily="font-omnes-regular"
											/>
											<BaseText
												text={`Status: ${errorMessage.status}`}
												additionalClasses=""
												size="text-md"
												color="text-navy-500"
												tag="p"
												fontFamily="font-omnes-regular"
											/>
										</>
									)}
								</div>
							) : isLoading ? (
								<motion.div
									initial={{ opacity: 1 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{
										duration: 0.4,
										ease: "easeInOut",
									}}
									className={loadingContainerClasses}
								>
									<div className="h-full">
										<LoadingMessage message={""} />
									</div>
								</motion.div>
							) : (
								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{
										ease: "easeOut",
										duration: 0.4,
									}}
									className="w-full"
								>
									<UpdateShipmentForm
										formClasses="flex flex-col w-full"
										optionsArray={checkOptionsArrays(
											optionsArray
										)}
										shipmentAddress={address}
										shipmentNumber={shipmentNumber}
									/>
								</motion.div>
							)}
						</Section>
					</Section>
				</div>
			</AnimatePresence>
		)
	);
};

export default EditShipmentPage;
